/**
 * AdminLlmConfig — admin overrides for the LLM provider + model per role.
 *
 * Two roles, mirroring services/llm.py:
 *   - chat  : Berta's actual replies (default Anthropic Sonnet)
 *   - admin : classifiers / extractors / re-engagement (default Anthropic Haiku)
 *
 * Hybrid model: env vars are the boot-time fallback; this page writes
 * overrides into app_config rows that _resolve_role() consults first.
 * Saving "use env default" deletes the override row.
 *
 * API keys + base URLs stay env-only on purpose — secrets shouldn't be
 * surfaced in the admin UI. The backend includes an api_keys_present map
 * so we can warn when an admin flips to a provider this deployment isn't
 * credentialed for.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { adminGet, adminPut } from './adminApi';

const styles = `
  .lc-section { margin-bottom: 28px; background: #fff; border: 1.5px solid #E8E3DC; border-radius: 14px; padding: 22px 24px; }
  .lc-section h2 { font-size: 1.05rem; font-weight: 700; color: #2C2C2C; margin: 0 0 6px; }
  .lc-section .lc-desc { font-size: 0.85rem; color: #666; margin-bottom: 18px; }
  .lc-row { display: grid; grid-template-columns: 130px 1fr 200px; gap: 12px; align-items: center; margin-bottom: 12px; }
  .lc-row label { font-size: 0.82rem; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: 0.04em; }
  .lc-input, .lc-select { border: 1.5px solid #E0D8D0; border-radius: 8px; padding: 8px 12px; font-size: 0.92rem; font-family: inherit; color: #2C2C2C; background: #FAFAF8; width: 100%; box-sizing: border-box; }
  .lc-input:focus, .lc-select:focus { outline: none; border-color: #E8845A; background: #fff; }
  .lc-input.dirty, .lc-select.dirty { border-color: #E8845A; background: #FFF7F2; }
  .lc-hint { font-size: 0.75rem; color: #888; }
  .lc-hint .lc-pill { display: inline-block; font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 20px; margin-left: 6px; }
  .lc-pill.override { background: #FFE9DC; color: #C04E18; }
  .lc-pill.env { background: #EAEAEA; color: #666; }
  .lc-pill.warn { background: #FFE5E5; color: #C03030; }
  .lc-actions { display: flex; gap: 10px; margin-top: 14px; }
  .lc-btn { background: #F5F0EB; color: #2C2C2C; border: 1.5px solid #E0D8D0; border-radius: 8px; padding: 8px 16px; font-size: 0.88rem; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; }
  .lc-btn:hover { background: #EDE7E0; }
  .lc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .lc-btn-primary { background: #E8845A; color: #fff; border-color: #E8845A; }
  .lc-btn-primary:hover { background: #D4724A; }
  .lc-btn-link { background: transparent; border: none; color: #1A6BB8; padding: 8px 6px; font-size: 0.85rem; cursor: pointer; font-family: inherit; }
  .lc-btn-link:hover { text-decoration: underline; }
  .lc-toast { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); padding: 10px 22px; border-radius: 10px; font-size: 0.9rem; font-weight: 500; z-index: 1000; }
  .lc-toast.success { background: #22A062; color: #fff; }
  .lc-toast.error { background: #C03030; color: #fff; }
  .lc-creds { font-size: 0.85rem; color: #555; margin: 14px 0 26px; padding: 12px 16px; background: #F8F4EE; border-radius: 10px; border: 1px solid #E8E3DC; }
  .lc-creds strong { color: #2C2C2C; }
  .lc-creds .lc-cred-yes { color: #22A062; font-weight: 700; }
  .lc-creds .lc-cred-no { color: #C03030; font-weight: 700; }

  @media (max-width: 700px) {
    .lc-row { grid-template-columns: 1fr; gap: 4px; }
    .lc-row label { margin-top: 8px; }
  }
`;

const ROLE_BLURB = {
  chat: "Berta's conversational replies. Latency-sensitive and reasoning-heavy — pair with a top-tier model.",
  admin: 'Classifiers, extractors, summarisers and re-engagement prompts. Short prompts → cheap model is fine.',
};

function PROVIDER_OPTIONS(providers) {
  return providers.map(p => ({ value: p, label: p === 'anthropic' ? 'Anthropic' : 'OpenAI-compatible' }));
}

function RoleCard({ role, providers, apiKeysPresent, onSaved }) {
  const { effective, override, env_fallback } = role;
  // Form state seeded from override (if any) or effective values so the
  // user sees the live config, with env_fallback rendered as the hint.
  const [provider, setProvider] = useState(override.provider || effective.provider);
  const [model, setModel] = useState(override.model || effective.model);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setProvider(override.provider || effective.provider);
    setModel(override.model || effective.model);
  }, [override.provider, override.model, effective.provider, effective.model]);

  const providerDirty = provider !== effective.provider;
  const modelDirty = model.trim() !== effective.model;
  const dirty = providerDirty || modelDirty;
  const credsMissing = !apiKeysPresent[provider];

  async function save() {
    setBusy(true);
    try {
      await adminPut('/llm-config', {
        role: role.role,
        provider,
        model: model.trim(),
      });
      onSaved('Saved.', 'success');
    } catch (e) {
      onSaved(e.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function resetField(field) {
    setBusy(true);
    try {
      const body = { role: role.role, provider: null, model: null };
      // Only clear the requested field; for the other field, send the
      // current effective so it stays put.
      if (field === 'provider') {
        body.provider = null;
        body.model = override.model || null;
      } else {
        body.model = null;
        body.provider = override.provider || null;
      }
      await adminPut('/llm-config', body);
      onSaved(`Reset ${field} to env default.`, 'success');
    } catch (e) {
      onSaved(e.message || 'Reset failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lc-section">
      <h2>{role.role === 'chat' ? 'Conversation (chat role)' : 'Classifiers (admin role)'}</h2>
      <div className="lc-desc">{ROLE_BLURB[role.role]}</div>

      <div className="lc-row">
        <label>Provider</label>
        <select
          className={`lc-select ${providerDirty ? 'dirty' : ''}`}
          value={provider}
          onChange={e => setProvider(e.target.value)}
          disabled={busy}
        >
          {PROVIDER_OPTIONS(providers).map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="lc-hint">
          env: <code>{env_fallback.provider}</code>
          {override.provider != null
            ? <span className="lc-pill override">override</span>
            : <span className="lc-pill env">from env</span>}
          {credsMissing && <span className="lc-pill warn">no API key</span>}
        </div>
      </div>

      <div className="lc-row">
        <label>Model</label>
        <input
          className={`lc-input ${modelDirty ? 'dirty' : ''}`}
          value={model}
          onChange={e => setModel(e.target.value)}
          disabled={busy}
          placeholder={env_fallback.model}
        />
        <div className="lc-hint">
          env: <code>{env_fallback.model}</code>
          {override.model != null
            ? <span className="lc-pill override">override</span>
            : <span className="lc-pill env">from env</span>}
        </div>
      </div>

      <div className="lc-actions">
        <button
          className="lc-btn lc-btn-primary"
          onClick={save}
          disabled={busy || !dirty || !model.trim()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {override.provider != null && (
          <button className="lc-btn-link" onClick={() => resetField('provider')} disabled={busy}>
            Reset provider to env
          </button>
        )}
        {override.model != null && (
          <button className="lc-btn-link" onClick={() => resetField('model')} disabled={busy}>
            Reset model to env
          </button>
        )}
      </div>
    </section>
  );
}

export default function AdminLlmConfig() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/llm-config');
      setData(res || {});
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load LLM config');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function showFeedback(message, kind) {
    setFeedback({ message, kind });
    setTimeout(() => setFeedback(null), 2400);
    load();  // refresh effective + override + env_fallback after every save
  }

  return (
    <AdminLayout title="LLM provider & model">
      <style>{styles}</style>

      {error && (
        <div className="lc-section" style={{ borderColor: '#F0CFCF', background: '#FFF3F3' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {data && (
        <>
          <div className="lc-creds">
            <strong>API keys on this deployment:</strong>{' '}
            Anthropic{' '}
            <span className={data.api_keys_present.anthropic ? 'lc-cred-yes' : 'lc-cred-no'}>
              {data.api_keys_present.anthropic ? '✓ present' : '✗ missing'}
            </span>{' · '}
            OpenAI{' '}
            <span className={data.api_keys_present.openai ? 'lc-cred-yes' : 'lc-cred-no'}>
              {data.api_keys_present.openai ? '✓ present' : '✗ missing'}
            </span>
            <br />
            <span style={{ fontSize: '0.78rem', color: '#888' }}>
              Provider keys come from env vars (<code>ANTHROPIC_API_KEY</code> /{' '}
              <code>OPENAI_API_KEY</code>) — rotating a key still requires a redeploy.
              Switching provider here without the matching key will fail at the next call.
            </span>
          </div>

          {data.roles.map(role => (
            <RoleCard
              key={role.role}
              role={role}
              providers={data.providers}
              apiKeysPresent={data.api_keys_present}
              onSaved={showFeedback}
            />
          ))}
        </>
      )}

      {feedback && (
        <div className={`lc-toast ${feedback.kind}`}>{feedback.message}</div>
      )}
    </AdminLayout>
  );
}
